import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ExternalLink, Fish } from "lucide-react";
import { navItems, club } from "../mock/mock";
import { Button } from "./ui/button";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const isHome = location.pathname === "/";
  // On home, header is transparent until scrolled; on subpages always solid.
  const solid = scrolled || !isHome || open;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-background/95 backdrop-blur-md shadow-sm border-b border-border"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={club.logo}
              alt={club.name}
              className="h-14 w-14 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="hidden sm:block leading-tight">
              <span
                className={`block font-serif text-xl font-600 ${
                  solid ? "text-primary" : "text-white"
                }`}
              >
                Fischerei Verein Fürth
              </span>
              <span
                className={`block text-[11px] tracking-[0.25em] uppercase ${
                  solid ? "text-muted-foreground" : "text-white/80"
                }`}
              >
                gegründet 1881
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`relative px-4 py-2 text-sm font-500 rounded-md transition-colors duration-200 ${
                    solid
                      ? active
                        ? "text-primary"
                        : "text-foreground/80 hover:text-primary"
                      : active
                      ? "text-white"
                      : "text-white/90 hover:text-white"
                  }`}
                >
                  {item.label}
                  {active && (
                    <span className="absolute left-4 right-4 -bottom-0.5 h-0.5 bg-accent rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="hidden lg:block">
            <a href={club.hejfish} target="_blank" rel="noopener noreferrer">
              <Button className="bg-accent hover:bg-accent/90 text-accent-foreground font-600 gap-2">
                Angelkarten online
                <ExternalLink className="h-4 w-4" />
              </Button>
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            className={`lg:hidden p-2 rounded-md ${
              solid ? "text-primary" : "text-white"
            }`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Menü"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-background border-t border-border shadow-lg">
          <nav className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1">
            {navItems.map((item) => {
              const active = location.pathname === item.to;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex items-center gap-2 px-3 py-3 rounded-md text-base font-500 transition-colors ${
                    active
                      ? "bg-secondary text-primary"
                      : "text-foreground/80 hover:bg-secondary"
                  }`}
                >
                  <Fish className="h-4 w-4 text-accent" />
                  {item.label}
                </Link>
              );
            })}
            <a
              href={club.hejfish}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2"
            >
              <Button className="w-full bg-accent hover:bg-accent/90 text-accent-foreground font-600 gap-2">
                Angelkarten online kaufen
                <ExternalLink className="h-4 w-4" />
              </Button>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
